import * as React from 'react'
import { Container } from '@amzn/dvui/dist/Container'
import { Link } from '@amzn/dvui/dist/Link'
import { Spinner } from '@amzn/dvui/dist/Spinner'

import { withFatalCheck } from '../error/withFatalCheck'
import { Episode } from '../episode/episode'
import { CollectionPagination } from '../collection/pagination'

import * as st from './episodeList.module.scss'
import * as stUtility from '../utilityClasses.module.scss'
import { CollectionType } from '../collection/collectionTypes'
import { TextLocalizer } from '../localizer/localizer'
import { IDetailPageState } from '../../reducers'
import { IRequestContext, sanitizedRequestContextSelector } from '../../selectors/requestContext'
import { requestEpisodes } from '../../utils/wps'
import { connect } from 'react-redux'
import { StringId } from '../localizer/stringId'
import { detailPagePath, MakeUrlArgs } from '../../utils/url'
import { episodesSeenSelector } from '../../selectors/actionFragment'
import { EXPANDER_THRESHOLD } from '../../reducers/collections'

export const EPISODE_START_INDEX = 0

export interface OwnProps {
  header?: string
  collectionType: CollectionType
  totalEpisodeSize: number
  cardTitleIds: string[]
}

export interface IStateProps {
  requestContext: IRequestContext
  pageTitleId: string
  isElcano: boolean
  useEpisodeListSortingT1: boolean
  useEpisodeListSortingT2: boolean
  episodesSeen: boolean
}

export type EpisodeListProps = OwnProps & IStateProps

export interface EpisodeListState {
  episodesHidden: boolean
}

class EpisodeListComp extends React.Component<EpisodeListProps, EpisodeListState> {
  constructor(props: EpisodeListProps) {
    super(props)
    this.state = {
      episodesHidden: props.cardTitleIds.length < props.totalEpisodeSize
    }
  }

  componentDidMount() {
    const { requestContext, pageTitleId, isElcano, useEpisodeListSortingT1, useEpisodeListSortingT2,
            collectionType, totalEpisodeSize, cardTitleIds, episodesSeen } = this.props

    if (collectionType === 'episodes' && cardTitleIds.length < totalEpisodeSize) {
      if (episodesSeen) {
        this.setState({
          episodesHidden: false
        })
      }
      /* For T1 we fetch the remaining episodes on Page load. If episodes seen and in T2, the we also fetch episodes */
      if (useEpisodeListSortingT1 || (useEpisodeListSortingT2 && episodesSeen)) {
        requestEpisodes(requestContext.rawContext, pageTitleId, isElcano, EXPANDER_THRESHOLD,
          totalEpisodeSize - EXPANDER_THRESHOLD)
      }
    }
  }

  onClickHandler = (e: React.MouseEvent<HTMLElement> | React.MouseEvent<HTMLAnchorElement>) => {
    const { requestContext, pageTitleId, isElcano, useEpisodeListSortingT2, totalEpisodeSize } = this.props

    e.preventDefault() /* Prevents redirect */
    /* For T2 we fetch the remaining episodes on Expansion */
    this.setState({ episodesHidden: false })
    if (useEpisodeListSortingT2) {
      requestEpisodes(requestContext.rawContext, pageTitleId, isElcano, EXPANDER_THRESHOLD,
        totalEpisodeSize - EXPANDER_THRESHOLD)
    }
  }

  render() {
    const { header, cardTitleIds, totalEpisodeSize, collectionType, useEpisodeListSortingT1, useEpisodeListSortingT2,
            pageTitleId, requestContext } = this.props

    const { episodesHidden } = this.state

    /* We show the spinner if every episode hasn't been added to the store yet */
    const showSpinner = cardTitleIds.length < totalEpisodeSize

    if (!collectionType) {
      return null
    }

    const cardComponents =
      cardTitleIds.map((titleId, idx) => {
        const episodeProps = {
          titleId,
          collectionType,
          epListIndex: `${collectionType}-${idx}`,
          isFirstEpisode: idx === 0,
          isBonus: collectionType === "bonus"
        }
        return (
          <React.Fragment key={idx}>
            <Episode {...episodeProps} />
          </React.Fragment>
        )
      })

    if ((cardComponents || []).length === 0) {
      return null
    }

    const paginationArgs = {
      totalEpisodeSize,
      collectionType,
    }

    /* Url for Core Experience */
    const params = {
      get: {
        "episodeListSize": `${totalEpisodeSize}`
      }
    } as MakeUrlArgs
    const pageUrl = requestContext.makeUrl(detailPagePath(pageTitleId), params)

    /* TREATMENT 1 & 2 */
    if ((useEpisodeListSortingT1 || useEpisodeListSortingT2) && collectionType === 'episodes') {
      return (
        <Container className={stUtility.pageSection}>
          { header &&
          <h1 className={st.episodeListHeader}>{header} ({totalEpisodeSize})</h1> }
          <ol className={st.episodeList}>
            {cardComponents.slice(EPISODE_START_INDEX, EXPANDER_THRESHOLD)}
            <span className={episodesHidden? st.hidden : ''}>
              {showSpinner?
                <div className={st.episodeSpinner}><Spinner /></div> :
                cardComponents.slice(EXPANDER_THRESHOLD)
              }
            </span>
          </ol>
          { episodesHidden &&
          <Link className={st.expanderLink} href={pageUrl} onClick={this.onClickHandler}>
              <TextLocalizer stringID={StringId.DV_DP_EPISODE_EXPANDER} replace={{ episodeCount: `${totalEpisodeSize}` }} />
          </Link> }
        </Container>
      )
    }

    /* CONTROL */
    return (
      <Container className={stUtility.pageSection}>
        { header &&
        <h1 className={st.episodeListHeader}>{header} ({totalEpisodeSize})</h1>}
        <ol className={st.episodeList}>{cardComponents}</ol>
        <CollectionPagination {...paginationArgs} />
      </Container>
    )
  }
}

const mapStateToProps = (state: IDetailPageState): IStateProps => ({
  requestContext: sanitizedRequestContextSelector(state),
  pageTitleId: state.pageTitleId,
  isElcano: Boolean(state.features.isElcano),
  useEpisodeListSortingT1: Boolean(state.features.useEpisodeListSortingT1),
  useEpisodeListSortingT2: Boolean(state.features.useEpisodeListSortingT2),
  episodesSeen: episodesSeenSelector(state)
})

export const EpisodeList = withFatalCheck(connect(mapStateToProps)(EpisodeListComp))
